import { RENDERING_DETAILS_TYPES } from "@core-ui/constants/RenderingDetails";
import classNames from "classnames";
import React, { memo, ReactElement, RefObject, useCallback, useMemo } from "react";

import Name from "./components/name";
import { Savings } from "./components/savings";
import { Value } from "./components/value";
import { RenderingDetails } from "@swiggy-private/cart-types";
import { AriaHidden, ScreenReaderOnly } from "@core-ui/components/accessibility";
import { ACCESSIBILITY_TEXTS } from "@core-ui/constants/BillDetails";
import { isEmpty } from "@swiggy-private/js-utils";
import { OneLogoFull, OneLiteLogoFull } from "@core-ui/widgets/swiggy-one-logo";

import styles from "../BillDetailItem.scss";

export interface DisplayProps {
    itemData: RenderingDetails;
    totalSavings?: number;
    discountType?: string;
    isInTooltip?: boolean;
    onItemClick?: (itemType: string) => void;
    onClickInfoIcon?: (key: string) => void;
    isOfferPack?: boolean;
    planAddedRef?: RefObject<HTMLDivElement>;
    isSuperItem?: (key: string) => boolean;
    isItemValueRupee?: (itemData: RenderingDetails) => boolean;
    isOneLiteItem?: (key: string) => boolean;
}

const Display = ({
    itemData,
    totalSavings = 0,
    discountType = "",
    isInTooltip = false,
    onItemClick,
    onClickInfoIcon,
    isOfferPack,
    planAddedRef,
    isSuperItem,
    isItemValueRupee,
    isOneLiteItem,
}: DisplayProps): ReactElement => {
    const key = itemData.key.toUpperCase();
    const containerClass = classNames({
        [styles.display]: true,
        [styles.displayGrandTotal]: key === RENDERING_DETAILS_TYPES.GRAND_TOTAL,
        [styles.displayInTooltip]: !!isInTooltip,
    });
    const WrapperClass = classNames({
        [styles.row]: true,
        [styles.rowAlignCenter]: isEmpty(itemData?.meta?.valueCtaText),
        [styles.rowAlignStart]: !isEmpty(itemData?.meta?.valueCtaText),
    });

    const handlItemClick = useCallback(() => {
        typeof onItemClick === "function" && onItemClick(itemData.key.toUpperCase());
    }, [itemData, onItemClick]);

    const isItemValueRupeeText = useMemo(() => isItemValueRupee?.(itemData), [itemData]);

    const getScreenReaderText = useMemo((): string => {
        return `${itemData.display_text} ${itemData.info_text} ${
            isItemValueRupeeText ? ACCESSIBILITY_TEXTS.RUPEE : ""
        }  ${isItemValueRupeeText ? itemData.value : ""}`;
    }, [itemData, isItemValueRupeeText]);

    const rowClassName = classNames({ [styles.rowName]: true, [styles.rowNameCenterAlign]: isOneLiteItem?.(key) });

    return (
        <div>
            <div className={containerClass}>
                <div style={{ position: "relative" }}>
                    <ScreenReaderOnly as="div" text={getScreenReaderText} />
                </div>
                <div className={WrapperClass}>
                    <AriaHidden as="div" className={rowClassName}>
                        {/* isOfferPack is drilled down as key is same for One & Offer Bundles */}
                        {isSuperItem?.(key) && !isOfferPack ? <OneLogoFull className={styles.icon} /> : null}
                        {isOneLiteItem?.(key) && !isOfferPack ? <OneLiteLogoFull className={styles.iconOneLite} /> : null}
                        <Name itemData={itemData} onClickInfoIcon={onClickInfoIcon} />
                    </AriaHidden>
                    <div className={styles.rowValue} ref={planAddedRef} aria-hidden={isItemValueRupeeText}>
                        <Value
                            itemKey={key}
                            itemData={itemData}
                            totalSavings={totalSavings}
                            isInTooltip={isInTooltip}
                            onItemClick={handlItemClick}
                        />
                        {!isEmpty(itemData?.meta?.valueCtaText) && (
                            <div
                                className={classNames(styles.displayInfoTextValue, styles.rowValueBullEyeXp)}
                                onClick={handlItemClick}
                                data-testid={`${itemData.key}_VALUE_CTA_TEXT`}
                            >
                                {itemData?.meta?.valueCtaText}
                            </div>
                        )}
                    </div>
                </div>

                <Savings discountType={discountType} key={key} totalSavings={totalSavings} />
            </div>
        </div>
    );
};

export default memo(Display);
